import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import { LOGO_FULL } from "@/lib/api";
import { findColour } from "@/lib/pascoColours";
import { formatDate } from "@/lib/dates";
import SignaturePad from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ShieldCheck, FileText, Droplet, Clock, XCircle } from "lucide-react";
import axios from "axios";

export default function SignDocket() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [comments, setComments] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [signedNow, setSignedNow] = useState(false);

  const base = `${API}/sign/${token}`;
  const fileSrc = (p) => `${base}/file/${p}`;

  useEffect(() => {
    axios.get(base)
      .then((r) => { setData(r.data); setName(r.data.customer_name || ""); setComments(r.data.customer_comments || ""); })
      .catch((e) => setError(e.response?.data?.detail || "This signing link is invalid or has expired."));
  }, [token]);

  const submit = async () => {
    if (!sig || !name.trim()) return;
    setBusy(true);
    try {
      await axios.post(base, { signature: sig, customer_name: name, customer_comments: comments });
      setSignedNow(true);
    } catch (e) { setError(e.response?.data?.detail || "Could not submit signature."); }
    finally { setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-navy font-heading font-semibold text-lg">Unable to open docket</p>
        <p className="text-slate-500 text-sm mt-2">{error}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-navy flex items-center justify-center text-white">Loading…</div>;

  const done = signedNow || data.status === "signed";
  const swatch = findColour(data.sealant_product, data.sealant_colour);
  const UNIT_LABELS = { lm: "lm", day: "day", each: "" };
  const qtyLabel = (c) => {
    if (c.qty === null || c.qty === undefined || c.qty === "") return "—";
    const u = c.unit in UNIT_LABELS ? UNIT_LABELS[c.unit] : (c.unit || "");
    return `${c.qty} ${u}`.trim();
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-navy px-5 py-5 flex flex-col items-center">
        <img src={LOGO_FULL} alt="True Seal Caulking" className="h-16 object-contain" />
      </header>

      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-4 pb-16">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center justify-between">
            <h1 className="font-heading font-bold text-navy text-xl">Service Completion Docket</h1>
            <span className="text-xs font-mono text-slate-400">{data.job_number}</span>
          </div>
          <p className="text-slate-500 text-sm mt-1">{data.site_address}</p>
          <a href={`${base}/docket`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 text-sm font-medium mt-3"><FileText className="w-4 h-4" />View full PDF</a>
        </div>

        {(data.before_photos?.length > 0 || data.after_photos?.length > 0) && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
            {["before_photos", "after_photos"].map((k) => data[k]?.length > 0 && (
              <div key={k}>
                <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">{k === "before_photos" ? "Before" : "After"} Photos</h4>
                <div className="grid grid-cols-3 gap-2">
                  {data[k].map((p, i) => <img key={i} src={fileSrc(p)} alt="" className="w-full h-24 object-cover rounded-lg border border-slate-200" />)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div><p className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1"><Droplet className="w-3.5 h-3.5" />Sealant Product</p><p className="font-medium text-navy mt-0.5">{data.sealant_product || "—"}</p></div>
            <div><p className="text-xs uppercase tracking-wider text-slate-400">Sealant Colour</p><p className="font-medium text-navy mt-0.5 flex items-center gap-2">{swatch && <span className="w-4 h-4 rounded-full border border-slate-300" style={{ background: swatch.hex }} />}{data.sealant_colour || "—"}</p></div>
          </div>
          {data.description_of_works && <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Description of Works Completed</p><p className="text-slate-600 whitespace-pre-line">{data.description_of_works}</p></div>}
        </div>

        {data.charge_codes?.length > 0 && (
          <div data-testid="sign-charge-codes" className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3">Works Performed / Charge Items</h4>
            <div className="divide-y divide-slate-100">
              <div className="flex items-center justify-between gap-4 pb-2 text-[11px] uppercase tracking-wider text-slate-400">
                <span>Description</span><span className="shrink-0">Quantity</span>
              </div>
              {data.charge_codes.map((c, i) => (
                <div key={i} data-testid={`sign-charge-code-${i}`} className="flex items-start justify-between gap-4 py-2 text-sm">
                  <span className="text-slate-700">{c.description}</span>
                  <span className="text-slate-500 shrink-0 font-medium">{qtyLabel(c)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {done ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-heading font-bold text-navy text-lg">Thank you — Docket Signed</h2>
            <p className="text-slate-500 text-sm mt-2">A signed copy has been sent to you and True Seal Caulking.</p>
            <p className="text-slate-400 text-xs mt-4 tracking-[0.2em] uppercase">Sealed Right. Built To Last.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><ShieldCheck className="w-5 h-5" />Client Authorisation</h3>
            <p className="text-sm text-slate-500 mt-2 mb-3">I confirm the works listed above have been completed to my satisfaction.</p>
            <label className="text-xs uppercase tracking-wider text-slate-400">Customer Name</label>
            <Input data-testid="sign-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 mb-4" placeholder="Your full name" />
            <label className="text-xs uppercase tracking-wider text-slate-400">Customer Comments <span className="text-slate-300 normal-case">(optional)</span></label>
            <Textarea data-testid="sign-comments-input" value={comments} onChange={(e) => setComments(e.target.value)} rows={3} className="mt-1 mb-4" placeholder="Add any comments about the completed works…" />
            <label className="text-xs uppercase tracking-wider text-slate-400">Signature</label>
            <div className="mt-1">
              {sig ? (
                <div className="border border-slate-200 rounded-xl p-3">
                  <img src={sig} alt="signature" className="h-20 object-contain" />
                  <button onClick={() => setSig("")} className="text-xs text-blue-700 mt-2">Redo signature</button>
                </div>
              ) : <SignaturePad onSave={setSig} saving={false} />}
            </div>
            <p className="text-xs text-slate-400 mt-2">Date: {formatDate(new Date())}</p>
            <Button data-testid="sign-submit-button" onClick={submit} disabled={busy || !sig || !name.trim()} className="w-full mt-4 bg-navy hover:bg-navy-light py-6 text-base">
              <CheckCircle2 className="w-5 h-5 mr-2" />{busy ? "Submitting…" : "Sign & Submit"}
            </Button>
            <p className="text-[11px] text-slate-400 text-center mt-3 flex items-center justify-center gap-1"><Clock className="w-3 h-3" />Secure electronic signature · link expires in 30 days</p>
          </div>
        )}
      </div>
    </div>
  );
}
